import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material'
import HandshakeIcon from '@mui/icons-material/Handshake'
import { FC, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { papelPrincipal } from '@/components/layout/menu'
import { oportunidadeService } from '@/services/oportunidadeService'
import { EncaminhamentoParceiro } from '@/types/oportunidade'
import { ReservaEstruturacao } from '@/types/estruturacao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { formatDate } from '@/utils/formatters'

interface Props {
  reserva: ReservaEstruturacao
}

const STATUS_ENC_LABEL: Record<EncaminhamentoParceiro['status'], string> = {
  ENVIADO: 'Aguardando retorno',
  ACEITO: 'Aceito pelo parceiro',
  RECUSADO: 'Recusado pelo parceiro',
}

/**
 * Etapa 5 — Parceiro: "Parceiro em definição" até `parceiroSelecionado` existir; depois
 * o nome e o encaminhamento formal ("ENC-n-v").
 *
 * O histórico de encaminhamentos (`GET /encaminhamentos`) é OPERADOR+ no backend — o
 * consultor NÃO chama (daria 403): para ele, o parceiro selecionado é o único dado de
 * parceiro que existe, e o texto explica o que o encaminhamento inclui.
 */
export const EtapaParceiro: FC<Props> = ({ reserva }) => {
  const { usuario } = useAuth()
  const papel = papelPrincipal(usuario?.permissoes)
  const ehBackOffice = papel === 'ADMIN' || papel === 'GERENTE' || papel === 'OPERADOR'

  const [encaminhamentos, setEncaminhamentos] = useState<EncaminhamentoParceiro[] | null>(null)

  useEffect(() => {
    let ativo = true
    if (ehBackOffice && reserva.parceiroSelecionadoId) {
      oportunidadeService
        .listarEncaminhamentos(reserva.id)
        .then((lista) => {
          if (ativo) setEncaminhamentos(lista)
        })
        .catch(() => {
          if (ativo) setEncaminhamentos([])
        })
    }
    return () => {
      ativo = false
    }
  }, [ehBackOffice, reserva.id, reserva.parceiroSelecionadoId])

  if (!reserva.parceiroSelecionadoId) {
    return (
      <EstadoDeLista
        estado="vazio"
        icone={<HandshakeIcon fontSize="inherit" />}
        mensagem="Parceiro em definição"
        detalhe="A Originei está definindo o parceiro ideal para esta operação. Você será avisado quando a seleção acontecer."
      />
    )
  }

  const ultimo = encaminhamentos?.[0] ?? null

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography
            variant="caption"
            component="div"
            sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em', mb: 0.5 }}
          >
            Parceiro selecionado
          </Typography>
          <Typography sx={{ fontWeight: 700 }}>{reserva.parceiroSelecionadoNome}</Typography>
          {reserva.parceiroSelecionadoEm && (
            <Typography variant="caption" color="text.secondary">
              selecionado em {formatDate(reserva.parceiroSelecionadoEm)}
            </Typography>
          )}
        </CardContent>
      </Card>

      {ehBackOffice ? (
        ultimo ? (
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  O encaminhamento inclui o pacote imutável da reserva
                </Typography>
                <Chip size="small" label={STATUS_ENC_LABEL[ultimo.status]} />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Protocolo {ultimo.protocolo} · v{ultimo.versao} · enviado em{' '}
                {formatDate(ultimo.enviadoEm)}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Alert severity="info">
            Ainda não há encaminhamento formal registrado para esta reserva.
          </Alert>
        )
      ) : (
        <Typography variant="body2" color="text.secondary">
          O encaminhamento ao parceiro inclui os dados da reserva e a documentação
          validada, em pacote imutável com protocolo — conduzido pela Originei.
        </Typography>
      )}
    </Box>
  )
}

export default EtapaParceiro
